import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { register, clearError } from '../store/slices/authSlice';
import Input from '../components/common/Input';
import PasswordInput from '../components/common/PasswordInput';
import Button from '../components/common/Button';
import apiClient from '../api/client';
import posthog from 'posthog-js';
import { trackRegisterPrefillViewed, trackUserRegistered } from '../lib/analytics';
import useSeoMeta from '../hooks/useSeoMeta';
import type { AuthResponse } from '../types';

type SignupStepId = 'email' | 'password' | 'terms';

const ACCOUNT_SETUP_HIGHLIGHTS = [
  'Create your account with just email, password, and consent.',
  'Choose a strong password with uppercase, lowercase, and a number.',
  'Use the password visibility toggle to double-check it instead of retyping it.',
  'Wallet connections happen after signup during guided onboarding.',
];

const RegisterPage: React.FC = () => {
  useSeoMeta(
    'Create Free Account — Pinecurl Crypto Portfolio Tracker',
    'Sign up free to track crypto wallets across 10+ chains, monitor DeFi risk, and optimise your taxes. No credit card required.'
  );
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get('email')?.trim() ?? '';
  const prefillSource = searchParams.get('source') === 'landing-quick-start'
    ? 'landing-quick-start'
    : null;
  const [email, setEmail] = useState(() => prefilledEmail);
  const [password, setPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const termsRef = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, error } = useAppSelector(
    (state) => state.auth
  );

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/onboarding');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (prefilledEmail.length > 0) {
        passwordRef.current?.focus();
        return;
      }

      emailRef.current?.focus();
    }
  }, [isAuthenticated, prefilledEmail]);

  useEffect(() => {
    if (!prefillSource || prefilledEmail.length === 0) {
      return;
    }

    trackRegisterPrefillViewed(prefillSource, /\S+@\S+\.\S+/.test(prefilledEmail));
  }, [prefillSource, prefilledEmail]);

  const trimmedEmail = email.trim();
  const hasValidEmail = /\S+@\S+\.\S+/.test(trimmedEmail);
  const signupSteps: Array<{ id: SignupStepId; label: string; complete: boolean }> = [
    { id: 'email', label: 'Use a valid email address', complete: hasValidEmail },
    {
      id: 'password',
      label: 'Choose a strong password',
      complete:
        password.length >= 8 &&
        /[A-Z]/.test(password) &&
        /[a-z]/.test(password) &&
        /[0-9]/.test(password),
    },
    { id: 'terms', label: 'Accept the Terms of Use and Privacy Policy', complete: termsAccepted },
  ];
  const completedSteps = signupSteps.filter((step) => step.complete).length;
  const missingRequirements = signupSteps
    .filter((step) => !step.complete)
    .map((step) => step.label);
  const firstIncompleteStep = signupSteps.find((step) => !step.complete) ?? null;
  const canSubmit = missingRequirements.length === 0;
  const progressPercent = (completedSteps / signupSteps.length) * 100;

  const focusStep = (stepId: SignupStepId) => {
    const target =
      stepId === 'email'
        ? emailRef.current
        : stepId === 'password'
          ? passwordRef.current
          : termsRef.current;

    if (!target) {
      return;
    }

    if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    target.focus({ preventScroll: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (!hasValidEmail) {
      setValidationError('Enter a valid email address');
      return;
    }

    // Match backend rules in services/03-auth-service/src/utils/password.ts so
    // users get fast, specific feedback before we hit the network and waste
    // a rate-limit slot on a 400.
    const passwordIssues: string[] = [];
    if (password.length < 8) passwordIssues.push('at least 8 characters');
    if (!/[A-Z]/.test(password)) passwordIssues.push('one uppercase letter');
    if (!/[a-z]/.test(password)) passwordIssues.push('one lowercase letter');
    if (!/[0-9]/.test(password)) passwordIssues.push('one number');
    if (passwordIssues.length > 0) {
      setValidationError(`Password must contain ${passwordIssues.join(', ')}.`);
      return;
    }

    if (!termsAccepted) {
      setValidationError('You must accept the Terms of Use and Privacy Policy to create an account');
      return;
    }

    const result = await dispatch(
      register({
        email: trimmedEmail,
        password,
      })
    );

    if (!register.fulfilled.match(result)) {
      return;
    }

    const authResponse: AuthResponse = result.payload;
    const userId = authResponse.user.id;
    const identifiedName = authResponse.user.displayName || trimmedEmail;

    // Identify first so the event is linked to the user profile.
    posthog.identify(userId, { email: trimmedEmail, name: identifiedName });
    trackUserRegistered(userId, trimmedEmail);

    // Record GDPR consents after successful registration
    const consentTypes = ['DATA_STORAGE', 'ANALYTICS', 'MARKETING'] as const;
    for (const consentType of consentTypes) {
      try {
        await apiClient.post(`/compliance/privacy/consent/${userId}`, {
          consentType,
          accepted: true,
          version: '1.0',
        });
      } catch {
        // Non-critical — log only, don't block registration
        console.warn(`Failed to record ${consentType} consent`);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-app px-4">
      <div className="max-w-md w-full">
        <div className="mb-4">
          <Link to="/" className="text-gray-400 hover:text-white text-sm flex items-center gap-1 transition-colors">
            ← Back to home
          </Link>
        </div>
        <div className="bg-surface border border-border rounded-xl p-8 shadow-lg">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-text-primary">
              <span className="flex items-center justify-center gap-2">
                <img src="/hedgehog-logo.png" alt="" className="w-10 h-10" />
                Pinecurl
                <span className="text-[10px] font-semibold bg-purple-600 text-white px-1.5 py-0.5 rounded uppercase tracking-wide">
                  Beta
                </span>
              </span>
            </h1>
            <p className="text-text-secondary text-sm mt-1">
              Create your free account. Email, password, and consent only.
            </p>
          </div>

          <div className="mb-6 rounded-xl border border-primary-500/20 bg-primary-500/10 px-4 py-4">
            <p className="text-sm font-semibold text-text-primary">Start in about 2 minutes</p>
            <ul className="mt-3 space-y-2 text-sm text-text-secondary">
              {ACCOUNT_SETUP_HIGHLIGHTS.map((highlight) => (
                <li key={highlight} className="flex items-start gap-2">
                  <span className="mt-0.5 text-primary-300" aria-hidden="true">
                    •
                  </span>
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          </div>

          {prefillSource ? (
            <div className="mb-6 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-4">
              <p className="text-sm font-semibold text-text-primary">
                Your email is already filled in
              </p>
              <p className="mt-2 text-sm text-text-secondary">
                We copied it from the landing page so this step only needs your password
                and consent before onboarding. You can still change the email before
                creating the account.
              </p>
            </div>
          ) : null}

          {(error || validationError) && (
            <div className="bg-red-500/10 border border-red-500/30 text-accent-red px-4 py-3 rounded-lg mb-6 text-sm">
              {error || validationError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <Input
              ref={emailRef}
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
            {prefillSource ? (
              <p className="text-xs text-text-muted -mt-3 mb-4">
                Prefilled from the landing page quick start.
              </p>
            ) : null}
            <PasswordInput
              ref={passwordRef}
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="new-password"
            />
            <p className="text-xs text-text-muted -mt-3 mb-4">
              At least 8 characters with uppercase, lowercase, and a number.
            </p>
            <p className="text-xs text-text-muted -mt-3 mb-4">
              Use the eye icon to verify your password instead of filling a second field.
            </p>

            <label className="flex items-start gap-3 mt-4 cursor-pointer">
              <input
                ref={termsRef}
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 accent-primary-500 w-4 h-4 shrink-0"
                required
              />
              <span className="text-sm text-text-secondary leading-snug">
                I have read and agree to the{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  className="text-primary-400 hover:text-primary-300 underline"
                >
                  Terms of Use
                </Link>{' '}
                and{' '}
                <Link
                  to="/privacy"
                  target="_blank"
                  className="text-primary-400 hover:text-primary-300 underline"
                >
                  Privacy Policy
                </Link>
                , including the financial disclaimer. I understand that Pinecurl does not
                provide financial advice.
              </span>
            </label>

            <div className="mt-4 mb-4 rounded-lg border border-border bg-surface-raised/60 px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-text-primary">
                  Finish account setup
                </p>
                <span className="text-xs text-text-muted">
                  {completedSteps}/{signupSteps.length} ready
                </span>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-app">
                <div
                  className="h-full rounded-full bg-primary-500 transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <ul className="mt-3 space-y-2 text-sm">
                {signupSteps.map((step) => (
                  <li
                    key={step.label}
                    className="flex items-center justify-between gap-3 text-text-secondary"
                  >
                    <span>{step.label}</span>
                    {step.complete ? (
                      <span className="rounded-full bg-primary-500/15 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-primary-300">
                        Ready
                      </span>
                    ) : step.id === firstIncompleteStep?.id ? (
                      <button
                        type="button"
                        onClick={() => focusStep(step.id)}
                        className="rounded-full border border-primary-500/30 bg-primary-500/10 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-primary-300 transition-colors hover:border-primary-400/50 hover:bg-primary-500/15 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                        aria-label={`Continue to ${step.label.toLowerCase()}`}
                      >
                        Continue
                      </button>
                    ) : (
                      <span className="rounded-full bg-surface px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-text-muted">
                        Pending
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              {!canSubmit && firstIncompleteStep ? (
                <p className="mt-3 text-xs text-text-muted">
                  Use <span className="text-text-primary">Continue</span> to jump to the
                  next required step.
                </p>
              ) : null}
            </div>

            <p className="text-xs text-text-muted mb-4" aria-live="polite">
              {canSubmit
                ? 'Everything is ready. You can create your account now.'
                : `Still needed: ${missingRequirements[0]}. Use Continue in the checklist to jump there.`}
            </p>

            <Button
              type="submit"
              className="w-full"
              isLoading={isLoading}
              disabled={!canSubmit}
              disabledReason={missingRequirements[0]}
            >
              Create free account
            </Button>
          </form>

          <p className="text-center text-text-secondary text-sm mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-400 hover:text-primary-300">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
